import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X, Printer } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable PDF preview modal.
 *
 * Fetches the PDF as a blob using the authenticated API client, renders it
 * inline in an iframe, and offers a Download button that reuses the same
 * blob (so we never hit the backend twice).
 */
export default function PdfPreviewModal({
  open,
  onClose,
  endpoint,
  params,
  filename,
  title,
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !endpoint) return;
    let cancelled = false;
    let created = null;
    setLoading(true);
    setBlobUrl(null);
    api
      .get(endpoint, { params, responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: "application/pdf" });
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
      })
      .catch((err) => {
        if (!cancelled) toast.error(apiError(err, "Preview failed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endpoint, JSON.stringify(params || {})]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("PDF downloaded");
  };

  const print = () => {
    const iframe = document.querySelector('[data-testid="pdf-preview-iframe"]');
    if (!iframe || !iframe.contentWindow) {
      toast.error("Preview not ready — try again in a moment");
      return;
    }
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      // Some browsers block cross-origin blob print in iframe; fall back
      // by opening the blob in a new tab where the user hits the browser print icon.
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        data-testid="pdf-preview-modal"
        className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0 bg-stone-50"
      >
        <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-white shrink-0">
          <DialogTitle className="font-serif text-xl text-slate-900">
            {title || "PDF preview"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 p-4 overflow-hidden">
          {loading && (
            <div
              data-testid="pdf-preview-loading"
              className="w-full h-full flex items-center justify-center text-slate-400"
            >
              Rendering PDF…
            </div>
          )}
          {!loading && blobUrl && (
            <iframe
              data-testid="pdf-preview-iframe"
              src={blobUrl}
              className="w-full h-full border border-slate-200 rounded-lg bg-white"
              title="PDF preview"
            />
          )}
          {!loading && !blobUrl && (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              Preview unavailable.
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-200 bg-white shrink-0 flex sm:justify-end gap-2">
          <Button
            data-testid="pdf-preview-close-btn"
            type="button"
            variant="outline"
            className="rounded-full h-10 px-5"
            onClick={onClose}
          >
            <X className="w-4 h-4 mr-2" /> Close
          </Button>
          <Button
            data-testid="pdf-preview-print-btn"
            type="button"
            variant="outline"
            onClick={print}
            disabled={!blobUrl}
            className="rounded-full h-10 px-5"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button
            data-testid="pdf-preview-download-btn"
            type="button"
            onClick={download}
            disabled={!blobUrl}
            className="rounded-full ns-btn-primary h-10 px-6"
          >
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
